import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CreditKeyMetrics } from '../../state/credits/credits.model';

type TransformFn = (payload: any) => string;

export interface Metric {
    value: number | string;
    type: 'number' | 'percent' | 'date' | 'string';
    class?: string;
    format?: string;
    prefix?: string | TransformFn;
    suffix?: string | TransformFn;
    label?: string;
    visible?: boolean;
    order?: number;
    excludeEmpty?: boolean; // Exclude metric value from presentation if not truthy
}

@Component({
    selector: 'app-key-metrics',
    templateUrl: './key-metrics.component.html',
    styleUrls: ['./key-metrics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KeyMetricsComponent {
    get config(): { [p: string]: Partial<Metric> } {
        return this._config;
    }
    @Input() isCreditsKeyMetrics: boolean = false;

    @Input() set config(value: { [p: string]: Partial<Metric> }) {
        this._config = value;
    }

    get metrics(): Array<Metric> | CreditKeyMetrics {
        const metrics = Object.keys(this._config).map((key) => ({
            ...(<Metric>this._config[key]),
            value:
                this._metrics && this._metrics[key]
                    ? this._metrics[key]
                    : this.getDefaultValue(<Metric>this._config[key]),
            prefix: this.getPrefix(this._config[key])
        }));

        // Exclude entries that have either null or undefined values
        return metrics.filter((metric) => this.include(metric));
    }

    /**
     * Guard to exclude items that are excluded from inclusion if there is no metric value.
     * @param metric
     * @private
     */
    private include(metric: Metric) {
        return !(metric.excludeEmpty && !metric.value);
    }

    private getDefaultValue(metric: Metric): any {
        if (metric.type === 'number' || metric.type === 'percent') {
            return 0;
        }
        return '';
    }

    @Input() set metrics(value: any) {
        this._metrics = value;
    }

    private _metrics: Array<Metric> = [];

    /** Type guard to check if the field is of type TransformFn */
    private static instanceOfTransformFn(field: any): field is TransformFn {
        return (field as TransformFn) !== undefined;
    }

    private getPrefix(config: Partial<Metric>): string | undefined {
        if (KeyMetricsComponent.instanceOfTransformFn(config?.prefix)) {
            return (config?.prefix as TransformFn)(this._metrics);
        }
        return config?.prefix;
    }

    private _config: { [key: string]: Partial<Metric> } = {};
    constructor() {}
}
